import { Room, RoomAvailable } from "colyseus.js"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import GameState from "../../../rooms/states/game-state"
import { throttle } from "../../../utils/function"
import { joinExistingPreparationRoom, joinLobbyRoom } from "../game/lobby-logic"
import { useAppDispatch, useAppSelector } from "../hooks"
import { disconnect } from "../network"
import {
  resetLobby,
  setLobbyTeamGameInvite,
  setLobbyTeamInvite
} from "../stores/LobbyStore"
import {
  lobbyTeamRespond,
  logOut,
  setErrorAlertMessage,
  setPendingGameId
} from "../stores/NetworkStore"
import { EventsMenu } from "./component/events-menu/events-menu"
import LeaderboardMenu from "./component/leaderboard/leaderboard-menu"
import { MainSidebar } from "./component/main-sidebar/main-sidebar"
import { Modal } from "./component/modal/modal"
import RoomMenu from "./component/room-menu/room-menu"
import { LobbyTeamWidget } from "./component/lobby-team/lobby-team-widget"
import PokemonPortrait from "./component/pokemon-portrait"
import { WalletBalanceWidget } from "./component/wallet/wallet-balance-widget"
import { DepositModal } from "./component/wallet/deposit-modal"
import { WithdrawModal } from "./component/wallet/withdraw-modal"
import { cc } from "./utils/jsx"
import { LocalStoreKeys, localStore } from "./utils/store"
import "./lobby.css"

export default function Lobby() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const lobby = useAppSelector((state) => state.network.lobby)
  const client = useAppSelector((state) => state.network.client)
  const networkError = useAppSelector((state) => state.network.error)
  const pendingGameId = useAppSelector((state) => state.network.pendingGameId)
  const gameRooms: RoomAvailable[] = useAppSelector(
    (state) => state.lobby.gameRooms
  )
  const lobbyTeamInvite = useAppSelector((state) => state.lobby.lobbyTeamInvite)
  const lobbyTeammate = useAppSelector((state) => state.lobby.lobbyTeammate)
  const lobbyTeamGameInvite = useAppSelector(
    (state) => state.lobby.lobbyTeamGameInvite
  )
  const showGameReconnect =
    pendingGameId != null && gameRooms.some((r) => r.roomId === pendingGameId)

  const { t } = useTranslation()

  const lobbyJoined = useRef<boolean>(false)
  useEffect(() => {
    if (!lobbyJoined.current) {
      joinLobbyRoom(dispatch, navigate)
      lobbyJoined.current = true
    }
  }, [lobbyJoined])

  // Auto-dismiss team invite after 30s to match server-side expiry
  useEffect(() => {
    if (lobbyTeamInvite) {
      const timer = window.setTimeout(() => {
        dispatch(setLobbyTeamInvite(null))
      }, 30000)
      return () => clearTimeout(timer)
    }
  }, [lobbyTeamInvite])

  const sessionToken = useAppSelector((state) => state.network.sessionToken)

  const signOut = useCallback(async () => {
    if (lobby?.connection.isOpen) {
      await lobby.leave()
    }
    await disconnect()
    dispatch(resetLobby())
    dispatch(logOut())
    navigate("/")
  }, [dispatch, lobby])

  const reconnectToGame = throttle(async function reconnectToGame() {
    if (sessionToken && pendingGameId) {
      const game: Room<GameState> = await client.joinById(pendingGameId, {
        sessionToken
      })
      localStore.set(
        LocalStoreKeys.RECONNECTION_GAME,
        { reconnectionToken: game.reconnectionToken, roomId: game.roomId },
        30
      )
      await Promise.allSettled([
        lobby?.connection.isOpen && lobby.leave(false),
        game.connection.isOpen && game.leave(false)
      ])
      dispatch(resetLobby())
      navigate("/game")
    }
  }, 1000)

  return (
    <main className="lobby">
      <MainSidebar
        page="main_lobby"
        leave={signOut}
        leaveLabel={t("sign_out")}
      />
      <LobbyTeamWidget />
      <div className="lobby-container">
        <MainLobby />
      </div>
      <Modal
        show={showGameReconnect}
        header={t("game-reconnect-modal-title")}
        body={t("game-reconnect-modal-body")}
        footer={
          <>
            <button className="bubbly green" onClick={reconnectToGame}>
              {t("yes")}
            </button>
            <button
              className="bubbly red"
              onClick={() => {
                dispatch(setPendingGameId(null))
              }}
            >
              {t("no")}
            </button>
          </>
        }
      ></Modal>
      <Modal
        show={lobbyTeamInvite != null}
        header={t("team_invite_title")}
        body={
          lobbyTeamInvite ? (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5em", padding: "1em", justifyContent: "center" }}>
              <PokemonPortrait avatar={lobbyTeamInvite.fromAvatar} style={{ width: 40, height: 40 }} />
              <span><strong>{lobbyTeamInvite.fromName}</strong> {t("team_invite_body")}</span>
            </div>
          ) : undefined
        }
        footer={
          <>
            <button
              className="bubbly green"
              onClick={() => {
                dispatch(lobbyTeamRespond(true))
                dispatch(setLobbyTeamInvite(null))
              }}
            >
              {t("yes")}
            </button>
            <button
              className="bubbly red"
              onClick={() => {
                dispatch(lobbyTeamRespond(false))
                dispatch(setLobbyTeamInvite(null))
              }}
            >
              {t("no")}
            </button>
          </>
        }
      />
      <Modal
        show={lobbyTeamGameInvite != null}
        header={t("team_game_invite_title")}
        body={t("team_game_invite_body")}
        footer={
          <>
            <button
              className="bubbly green"
              onClick={() => {
                if (lobbyTeamGameInvite && client) {
                  joinExistingPreparationRoom(
                    lobbyTeamGameInvite,
                    client,
                    lobby,
                    dispatch,
                    navigate,
                    undefined,
                    lobbyTeammate?.uid
                  )
                }
                dispatch(setLobbyTeamGameInvite(null))
              }}
            >
              {t("yes")}
            </button>
            <button
              className="bubbly red"
              onClick={() => dispatch(setLobbyTeamGameInvite(null))}
            >
              {t("no")}
            </button>
          </>
        }
      />
      <Modal
        show={networkError != null}
        onClose={() => dispatch(setErrorAlertMessage(null))}
        className="is-dark basic-modal-body"
        body={<p style={{ padding: "1em" }}>{networkError}</p>}
      />
    </main>
  )
}

function MainLobby() {
  const [activeSection, setActive] = useState<string>("leaderboard")
  const [showDeposit, setShowDeposit] = useState(false)
  const [showWithdraw, setShowWithdraw] = useState(false)
  const { t } = useTranslation()

  // Auto-open deposit modal on Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const stripeDeposit = params.get("stripe_deposit")
    if (stripeDeposit) {
      setShowDeposit(true)
    }
  }, [])

  return (
    <div className="main-lobby">
      <nav className="main-lobby-nav">
        <ul>
          <li
            onClick={() => setActive("leaderboard")}
            className={cc({ active: activeSection === "leaderboard" })}
          >
            <img width={32} height={32} src={`assets/ui/leaderboard.svg`} />
            {t("leaderboard")}
          </li>
          <li
            onClick={() => setActive("rooms")}
            className={cc({ active: activeSection === "rooms" })}
          >
            <img width={32} height={32} src={`assets/ui/room.svg`} />
            {t("rooms")}
          </li>
          <li
            onClick={() => setActive("events")}
            className={cc({ active: activeSection === "events" })}
          >
            <img width={32} height={32} src={`assets/ui/chat.svg`} />
            {t("events")}
          </li>
        </ul>
      </nav>
      <section
        className={cc("leaderboard", {
          active: activeSection === "leaderboard"
        })}
      >
        <LeaderboardMenu />
      </section>
      <section className={cc("rooms", { active: activeSection === "rooms" })}>
        <RoomMenu />
      </section>
      <section
        className={cc("events-column", {
          active: activeSection === "events"
        })}
      >
        <WalletBalanceWidget
          onDeposit={() => setShowDeposit(true)}
          onWithdraw={() => setShowWithdraw(true)}
        />
        <EventsMenu />
      </section>
      <DepositModal
        show={showDeposit}
        onClose={() => setShowDeposit(false)}
      />
      <WithdrawModal
        show={showWithdraw}
        onClose={() => setShowWithdraw(false)}
      />
    </div>
  )
}
